import {
  Directive,
  ElementRef,
  EventEmitter,
  Injectable,
  Input,
  NgModule,
  OnInit,
  OnDestroy
} from "@angular/core";
import * as _ from "lodash";

@Injectable()
export class InternationalisationService {

  public static get locale(): string { return InternationalisationService.__locale; }
  private static __locale = "de";

  public get localeChanged(): EventEmitter<any> {
    return this._localeChanged;
  }
  private _localeChanged: EventEmitter<any> = new EventEmitter();

  private _initialized = false;
  private _locale = "de"; // todo
  private _locales = {};

  /**
   * Get the name of the currently set locale
   * @returns {string} The current locale's name
   */
  getCurrentLocale(): string {
    return this._locale;
  }

  /**
   * Get the current locale's translation value for the specified key
   * @param key The translation key
   * @returns {string} The corresponding translation value
   */
  getCurrentLocaleValue(key: string): string {
    const currentLocale = this.getCurrentLocale();

    if (!this.validateIdString(currentLocale)) {
      return key; // return in error case the key
    }

    const currentMap = this._locales[currentLocale];

    if (!currentMap) {
      this.err("Locale '" + currentLocale + "' is not configured and cannot be used.");
      return key; // return in error case the key
    }

    let value: any = this.getNestedValue(currentMap, key);

    if (!value && currentLocale !== "en") {
      const currentDefaultMap = this._locales["en"];
      value = this.getNestedValue(currentDefaultMap, key)
    }
    if (typeof value !== "string") {
      this.err("Locale '" + currentLocale + "' does not provide a translation for the key '" + key + "'.");
      return key; // return in error case the key
    }

    return value;
  }

  /**
   * Get the translation value for the nested key
   * @param languageMap The translation map
   * @param key The translation nested key
   * @returns {string} The corresponding translation value
   */
  private getNestedValue(languageMap: {}, key: string): string {
    let value: any = null;
    // Language kann aus Schachteln bestehen die per '.' referenziert werden können, wie "phStatus.Error"
    if (key.indexOf(".") > -1) {
      const keySplit = key.split(".");
      let temp_value = languageMap;
      for (let i = 0; i < keySplit.length; i++) {
        temp_value = temp_value[keySplit[i]];
        if (!temp_value) {
          break; // Kette unvollständig
        } else if (typeof temp_value === "string") {
          if (i !== keySplit.length) {
            break; // Ist nicht das letzte Kettenglied
          } else {
            value = temp_value;
          }
        }
      }
    } else {
      value = languageMap[key];
    }
    return value;
  }

  /**
 * Get the locale's translation value for the specified locale and key
 * @param local The local id
 * @param key The translation key
 * @returns {string} The corresponding translation value
 */
  getLocaleValue(local: string, key: string): string {
    if (!this.validateIdString(local)) {
      return key; // return in error case the key
    }

    const currentMap = this._locales[local];

    if (!currentMap) {
      this.err("Locale '" + local + "' is not configured and cannot be used.");

      return key; // return in error case the key
    }

    let value: any = null;

    if (key.indexOf(".") > -1) {
      const keySplit = key.split(".");

      value = currentMap;

      for (let i = 0; i < keySplit.length; i++) {
        value = value[keySplit[i]];
      }
    } else {
      value = currentMap[key];
    }

    if (!value) {
      this.err("Locale '" + local + "' does not provide a translation for the key '" + key + "'.");
      return key; // return in error case the key
    }

    return value;
  }

  /**
   * Get the current locale's translation value for the specified key in HTML safe encoding
   * @param key The translation key
   * @returns {string} The corresponding translation value, but HTML encoded
   */
  getCurrentLocaleValueSafe(key: string): string {
    return _.escape(this.getCurrentLocaleValue(key));
  }

  /**
   * Set the current locale
   * @param idString The locale's name
   * @returns {boolean} false, if an error occured, otherwise undefined
   */
  setCurrentLocale(idString: string): boolean {
    if (!this.validateIdString(idString)) {
      return false;
    }

    if (!this._locales[idString]) {
      this.err("Locale '" + idString + "' is not configured and cannot be set.");
      return false;
    }

    this._locale = idString;
    InternationalisationService.__locale = this._locale;
    this._localeChanged.emit();

    this.conditionalInit();
    return true;
  }

  /**
   * Add multiple locales at one
   * @param locales An object of locales. Each key is a locale's name, the corresponding value its translation map.
   */
  setLocales(locales: Object): void {
    for (const idString in locales) {
      if (locales.hasOwnProperty(idString)) {
        this.addLocale(idString, locales[idString], true);
      }
    }
    // trigger localeChnaged, to refresh the Languages after setLocales
    this._localeChanged.emit();

    this.conditionalInit();
  }

  /**
   * Add a locale
   * @param idString The locale's name
   * @param map The locale's translation map. An object.
   * @param noInit Do not call initialize function (used for bulk operations). Not to be used manually, or unexpected results may happen!
   */
  private addLocale(idString: string, map: Object, noInit?: boolean): void {
    if (!this.validateIdString(idString)) {
      return;
    }

    if (typeof map !== "object") {
      this.err("Translation map for locale '" + idString + "' has to be an object.");
      return;
    }

    if (this._locales[idString]) {
      this.warn("Locale '" + idString + "' is already configured. Old values are being overridden.");
      return;
    }

    this._locales[idString] = map;

    if (noInit !== true) {
      this.conditionalInit();
    }
  }

  /**
   * Call the init function, in case this has not happened yet
   */
  private conditionalInit(): void {
    if (!this._initialized) {
      this.init();
    }
  }

  /**
   * Display an error
   * @param msg The message text
   */
  private err(msg: string): void {
    console.error("Internationalisation: " + msg);
  }

  /**
   * Get the name of the first locale that was added to the locale list
   * @returns {string} The first locale's name
   */
  private getFirstLocaleIdString(): string {
    for (const idString in this._locales) {
      if (this._locales.hasOwnProperty(idString)) {
        return idString;
      }
    }
  }

  /**
   * Initialize awInternationalisation (not to be called manually, or unexpected results may happen!)
   */
  private init(): void {
    this._initialized = true;

    const defaultLocale = "de"; // todo hier könnte man die language aud der Config des Customers verwenden

    if (this.validateIdString(defaultLocale, true)) { // do not display warning
      this.setCurrentLocale(defaultLocale);
      return;
    }
    // fallback defaultLocale not found use First
    // try set locale to first locale
    this.setCurrentLocale(this.getFirstLocaleIdString());
  }

  /**
   * Validate if the specified string is a valid locale identifier
   * @param idString The identifier to validate
   * @param silent Do not display a warning in case validation fails (default false)
   * @returns {boolean} The identifier is valid
   */
  private validateIdString(idString: string, silent?: boolean): boolean {
    if (!idString || typeof idString !== "string" || !idString.length) {
      if (silent !== true) {
        this.warn("'" + idString + "' is not a valid locale identifier.");
      }

      return false;
    }

    return true;
  }

  /**
   * Display a warning
   * @param msg The message text
   */
  private warn(msg: string): void {
    console.warn("Internationalisation: " + msg);
  }

}

/** langkey usage like <xxx langkey="phModul_Key">[repalce inner HTML with lang_value]</xxx> */
@Directive({
  selector: "[langkey]"
})
export class InternationalisationDirective implements OnInit, OnDestroy {
  @Input("langkey") private key: string;

  private localeChangedSubscription: any;

  constructor(private internationalisationService: InternationalisationService, private element: ElementRef) { }

  ngOnInit(): void {
    const nativeElement = this.element.nativeElement;

    let updateContent: () => void;

    (updateContent = () => {
      nativeElement.innerHTML = this.internationalisationService.getCurrentLocaleValue(this.key);
    })();

    this.localeChangedSubscription = this.internationalisationService.localeChanged.subscribe(updateContent);
  }

  ngOnDestroy(): void {
    if (this.localeChangedSubscription) {
      this.localeChangedSubscription.unsubscribe();
    }
  }
}

/** langkeysafe usage like <xxx langkeysafe="phModul_Key">[repalce inner HTML with HTML encoded lang_value]</xxx> */
@Directive({
  selector: "[langkeysafe]"
})
export class InternationalisationSafeDirective implements OnInit, OnDestroy {
  @Input("langkeysafe") private key: string;

  private localeChangedSubscription: any;

  constructor(private internationalisationService: InternationalisationService, private element: ElementRef) { }

  ngOnInit(): void {
    const nativeElement = this.element.nativeElement;

    let updateContent: () => void;

    (updateContent = () => {
      nativeElement.innerHTML = this.internationalisationService.getCurrentLocaleValueSafe(this.key);
    })();

    this.localeChangedSubscription = this.internationalisationService.localeChanged.subscribe(updateContent);
  }

  ngOnDestroy(): void {
    if (this.localeChangedSubscription) {
      this.localeChangedSubscription.unsubscribe();
    }
  }
}


@NgModule({
  declarations: [InternationalisationDirective, InternationalisationSafeDirective],
  providers: [InternationalisationService],
  exports: [InternationalisationDirective, InternationalisationSafeDirective]
})
export class InternationalisationModule { }
